import React from "react";
import { useI18n } from "@/i18n";
import NewsletterForm from "@/components/NewsletterForm";

export default function Home() {
  const { t, dict } = useI18n();

  return (
    <>
      {/* HERO */}
      <section className="relative bg-white overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" aria-hidden="true" />
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16 lg:pb-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-px bg-[var(--brand-terracotta)]" />
                <span className="font-mono-brand text-[11px] uppercase tracking-[0.24em] text-[var(--brand-navy)]/70">
                  {t("hero.eyebrow")}
                </span>
              </div>
              <h1 className="font-serif-brand text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[var(--brand-navy)] font-semibold mb-6">
                {t("hero.title")}
              </h1>
              <p className="text-lg text-[var(--brand-ink)]/85 leading-relaxed mb-4">{t("hero.lead")}</p>
              <p className="text-base text-[var(--brand-ink)]/75 leading-relaxed mb-4">{t("hero.sub")}</p>
              <p className="text-base text-[var(--brand-ink)]/75 leading-relaxed mb-8">{t("hero.closing")}</p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#newsletter"
                  className="inline-flex items-center bg-[var(--brand-terracotta)] hover:bg-[var(--brand-terracotta-2)] text-white px-6 py-3 font-medium"
                  data-testid="hero-cta-primary"
                >
                  {t("hero.ctaPrimary")}
                </a>
                <a
                  href="#plataforma"
                  className="inline-flex items-center border border-[var(--brand-navy)] text-[var(--brand-navy)] hover:bg-[var(--brand-navy)] hover:text-white px-6 py-3 font-medium"
                  data-testid="hero-cta-secondary"
                >
                  {t("hero.ctaSecondary")}
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative border border-[var(--brand-line)]">
                <img
                  src="/images/hero-argentina.jpg"
                  alt={t("hero.imageAlt")}
                  className="w-full h-[420px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute left-0 bottom-0 right-0 bg-[var(--brand-navy)]/95 text-white px-5 py-4">
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-white/60 mb-1">
                    {t("common.argentina")} · 2026
                  </div>
                  <div className="text-sm text-white/95 leading-snug">
                    {t("common.heroCaption")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 border-t border-[var(--brand-line)]">
            {Object.entries(dict.hero.stats).map(([k, s]) => (
              <div
                key={k}
                className="py-6 lg:py-8 px-4 border-b lg:border-b-0 lg:border-r border-[var(--brand-line)] last:border-r-0"
                data-testid={`stat-${k}`}
              >
                <div className="font-serif-brand text-3xl lg:text-4xl text-[var(--brand-navy)] font-semibold">{s.value}</div>
                <div className="text-xs text-[var(--brand-ink)]/70 mt-2 leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY / PLATAFORMA */}
      <section id="plataforma" className="paper-grain">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[var(--brand-terracotta)]" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.24em] text-[var(--brand-navy)]/70">
                {t("nav.about")}
              </span>
            </div>
            <h2 className="font-serif-brand text-3xl lg:text-4xl text-[var(--brand-navy)] font-semibold mb-4">
              {t("why.title")}
            </h2>
            <p className="text-[var(--brand-ink)]/75">{t("why.intro")}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {dict.why.items.map((item, i) => (
              <article
                key={i}
                className="bg-white border border-[var(--brand-line)] p-6 lg:p-8"
                data-testid={`why-card-${i}`}
              >
                <div className="font-mono-brand text-xs text-[var(--brand-terracotta)] tracking-[0.24em] mb-4">
                  0{i + 1}
                </div>
                <h3 className="font-serif-brand text-xl text-[var(--brand-navy)] font-semibold mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-[var(--brand-ink)]/80 leading-relaxed">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section id="sectores" className="bg-white">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-px bg-[var(--brand-terracotta)]" />
                <span className="font-mono-brand text-[11px] uppercase tracking-[0.24em] text-[var(--brand-navy)]/70">
                  {t("nav.sectors")}
                </span>
              </div>
              <h2 className="font-serif-brand text-3xl lg:text-4xl text-[var(--brand-navy)] font-semibold mb-3">
                {t("sectors.title")}
              </h2>
              <p className="text-[var(--brand-ink)]/75">{t("sectors.intro")}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {dict.sectors.cards.map((card, i) => {
              const imgs = ["/images/vaca-muerta.jpg", "/images/lithium-flats.jpg", "/images/mining-infra.jpg"];
              return (
                <article key={i} className="border border-[var(--brand-line)]" data-testid={`sector-card-${i}`}>
                  <div className="relative">
                    <img src={imgs[i]} alt={card.imageAlt} className="w-full h-56 object-cover" />
                    <div className="absolute top-4 left-4 bg-[var(--brand-navy)] text-white px-3 py-1 font-mono-brand text-[10px] uppercase tracking-[0.2em]">
                      {card.tag}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif-brand text-xl text-[var(--brand-navy)] font-semibold mb-3">
                      {card.title}
                    </h3>
                    <p className="text-sm text-[var(--brand-ink)]/80 leading-relaxed">{card.text}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="proceso" className="bg-[var(--brand-navy)] text-white relative overflow-hidden">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 relative">
          <div className="max-w-3xl mb-12">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[var(--brand-terracotta)]" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.24em] text-white/60">
                {t("nav.process")}
              </span>
            </div>
            <h2 className="font-serif-brand text-3xl lg:text-4xl font-semibold mb-3">{t("process.title")}</h2>
            <p className="text-white/75">{t("process.intro")}</p>
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {dict.process.steps.map((step, i) => (
              <li
                key={i}
                className="bg-white/[0.04] border border-white/10 p-6"
                data-testid={`process-step-${i}`}
              >
                <div className="font-mono-brand text-[var(--brand-terracotta-2)] text-sm tracking-widest mb-4">
                  {step.n}
                </div>
                <h3 className="font-serif-brand text-lg font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/75 leading-relaxed">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <img
              src="/images/analytics.jpg"
              alt={t("transparency.imageAlt")}
              className="w-full h-[420px] object-cover border border-[var(--brand-line)]"
              data-testid="transparency-image"
            />
          </div>
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[var(--brand-terracotta)]" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.24em] text-[var(--brand-navy)]/70">
                {t("common.dataGovernance")}
              </span>
            </div>
            <h2 className="font-serif-brand text-3xl lg:text-4xl text-[var(--brand-navy)] font-semibold mb-4">
              {t("transparency.title")}
            </h2>
            <p className="text-[var(--brand-ink)]/80 mb-4 leading-relaxed">{t("transparency.p1")}</p>
            <p className="text-[var(--brand-ink)]/80 mb-6 leading-relaxed">{t("transparency.p2")}</p>
            <ul className="space-y-3">
              {dict.transparency.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[var(--brand-ink)]/85">
                  <span className="mt-2 w-2 h-2 bg-[var(--brand-terracotta)] shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-[var(--brand-navy-2)] relative">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <img src="/images/salt-flats.jpg" alt="" className="w-full h-full object-cover mix-blend-multiply" />
        </div>
        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 text-white">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[var(--brand-terracotta)]" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.24em] text-white/60">
                {t("newsletter.eyebrow")}
              </span>
            </div>
            <h2 className="font-serif-brand text-3xl lg:text-4xl font-semibold mb-4">{t("newsletter.title")}</h2>
            <p className="text-white/80 leading-relaxed">{t("newsletter.lead")}</p>
          </div>
          <div className="lg:col-span-7">
            <NewsletterForm />
          </div>
        </div>
      </section>
    </>
  );
}
